import React from "react";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonPinCircleOutlinedIcon from '@mui/icons-material/PersonPinCircleOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import FeaturedPlayListOutlinedIcon from '@mui/icons-material/FeaturedPlayListOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import DirectionsBoatTwoToneIcon from '@mui/icons-material/DirectionsBoatTwoTone';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PhonelinkLockOutlinedIcon from '@mui/icons-material/PhonelinkLockOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import _ from "lodash";

const buildDashboardMenuItem = (companyId) => {
    if (companyId === 80) {
        return {
            uri: "/dashboard",
            label: 'sidebar.menuItem.dashboard',
            type: "nav-item",
            icon: <DashboardOutlinedIcon sx={{fontSize: 20}}/>
        };
    }

    return {
        uri: "/business-dashboard",
        label: 'sidebar.menuItem.business_dashboard',
        type: "nav-item",
        icon: <AssessmentOutlinedIcon sx={{fontSize: 20}}/>
    };
};

// Lo nuevo en Haulers, para cualquiera con sesion: cada tanda puede traer un video y un PDF.
const buildNovedadesMenuItem = () => ({
    uri: "/novedades",
    label: 'Novedades',
    type: "nav-item",
    icon: <CampaignOutlinedIcon sx={{ fontSize: 20 }} />
});

// Etiquetas por imprimir de los viajes. Por ahora solo superadmin.
const buildEtiquetasMenuItem = () => ({
    uri: "/etiquetas",
    label: 'Etiquetas',
    type: "nav-item",
    icon: <QrCode2Icon sx={{ fontSize: 20 }} />
});

const menus = (user) => {
    var out = [
        {
            label: 'sidebar.menu.home',
            type: "section",
            children: []
        },
    ];

    // El backend arma los permisos con user.current_profiles (ver Ability), o sea
    // solo los perfiles de la empresa activa. El menu tiene que mirar los mismos:
    // si no, quien es customer_admin en una empresa y driver en otra sigue viendo
    // las opciones de admin despues de rotar, y se come un 403 al guardar.
    // superadmin es la excepcion, igual que is_global_superadmin en el backend:
    // vale en todas las empresas.
    const currentCompanyId = user?.current_company?.id ?? user?.current_company_id;
    const isGlobalSuperadmin = _.some(user?.profiles, { name: 'superadmin' });
    // El Pulso muestra datos de todas las empresas: solo el equipo de Kargotools, con el mismo
    // criterio que User#global_superadmin? en el backend (hay superadmins de clientes).
    const isKargotools = isGlobalSuperadmin && /@kargotools\.com$/i.test(user?.email || '');

    const profileNames = (() => {
        if (!user || !user.profiles) return [];
        // Sin empresa activa no hay con que filtrar; se cae al comportamiento
        // viejo antes que dejar el menu en blanco.
        if (!currentCompanyId) return _.uniq(_.map(user.profiles, 'name'));

        const names = _.chain(user.profiles)
            .filter((profile) => `${profile.company_id}` === `${currentCompanyId}`)
            .map('name')
            .value();

        return _.uniq(isGlobalSuperadmin ? [...names, 'superadmin'] : names);
    })();

    const hasProfile = (profileName) => _.includes(profileNames, profileName);

    const hasSubscriptionFeature = (featureKey) => {
        const capabilities = user?.current_company?.subscription_capabilities;
        if (!capabilities || Object.keys(capabilities).length === 0) return true;
        return Boolean(capabilities[featureKey]?.enabled);
    };

    const canAccessUsers = () => {
        return hasSubscriptionFeature('usuariosWeb') && (hasProfile('superadmin') || hasProfile('customer_admin') || hasProfile('supervisor'));
    };

    // Un telefono por cuenta de conductor: quien administra a los conductores aprueba los cambios
    // de telefono. Ver hermes-backend app/services/licensing/device_binding.rb.
    const canAccessDevices = () => {
        return hasSubscriptionFeature('usuariosMobile') && (hasProfile('superadmin') || hasProfile('customer_admin') || hasProfile('supervisor'));
    };

    // Posiciones e Inventario se muestran solo a superadmin.
    //
    // El backend sigue teniendo permisos más amplios (bodeguero y supervisor
    // administran las posiciones; picker y vendedor solo las ven), así que esto
    // cambia lo que se ve en el menú, no lo que se puede hacer: quien llegue por
    // URL a /warehouse o /inventory las sigue abriendo, igual que pasa hoy con
    // /logs y /subscriptions. Si hace falta cerrarlas de verdad, va en el
    // backend, no acá.
    const canAccessWarehouse = () => hasProfile('superadmin');

    // El inventario del ERP lo ve quien ve el tablero: es la misma informacion
    // (producto, stock, posicion) entrando por el catalogo en vez del plano.
    // El maestro de SKU cuelga de aca como submenu y queda tambien solo para
    // superadmin; /skus sigue abriendo por URL para los perfiles que el backend deja.
    const buildInventoryMenuItem = () => ({
        label: 'SKU',
        type: "collapsible",
        icon: <QrCode2OutlinedIcon sx={{ fontSize: 20 }} />,
        children: [
            {
                uri: "/inventory",
                label: 'sidebar.menuItem.inventory',
                type: "nav-item",
                icon: <Inventory2OutlinedIcon sx={{ fontSize: 18, ml: 1 }} />
            },
            {
                uri: "/skus",
                label: 'sidebar.menuItem.skus',
                type: "nav-item",
                icon: <ReportProblemOutlinedIcon sx={{ fontSize: 18, ml: 1 }} />
            }
        ]
    });

    const buildWarehouseMenuItem = () => ({
        uri: "/warehouse",
        label: 'sidebar.menuItem.warehouse',
        type: "nav-item",
        icon: <GridViewOutlinedIcon sx={{ fontSize: 20 }} />
    });

    // Documentos: acuse de recibo y regularizacion de las entregas.
    //
    // Solo superadmin, igual que allowed_profile? en DocumentsController. Se repite aca solo
    // para no ofrecer una pantalla que va a contestar 403; quien entre por URL lo frena el
    // backend, igual que pasa con /logs.
    const canAccessDocuments = () => hasProfile('superadmin');

    const buildDocumentsMenuItem = () => ({
        uri: "/documents",
        label: 'Documentos',
        type: "nav-item",
        icon: <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
    });

    // Utilidades: herramientas del equipo que miran a todos los clientes a la vez (Pulso, estado
    // de integraciones, reportes de la app). Solo el equipo de Kargotools, con el mismo criterio que
    // User#global_superadmin? en el backend, que es lo que piden sus endpoints.
    const buildUtilidadesSection = () => ({
        label: 'Utilidades',
        type: "section",
        children: [
            {
                uri: "/estado_integraciones",
                label: 'Estado de integraciones',
                type: "nav-item",
                icon: <HubOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
                uri: "/pulso",
                label: 'Pulso',
                type: "nav-item",
                icon: <MonitorHeartOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
                uri: "/bug_reports",
                label: 'Reportes de la app',
                type: "nav-item",
                icon: <BugReportOutlinedIcon sx={{ fontSize: 20 }} />
            }
        ]
    });

    if (user) {
        const isAdminAndDriver = hasProfile('customer_admin') && hasProfile('driver');
        const isSupervisorAndDriver = hasProfile('supervisor') && hasProfile('driver');
        const isFinanzasOnly = hasProfile('finanzas') && !_.some(profileNames, (profileName) => (
            ['superadmin', 'customer_admin', 'supervisor', 'planner', 'seller', 'picker', 'reader', 'driver', 'transportista', 'warehouse'].includes(profileName)
        ));

        if (isFinanzasOnly) {
            out[0].children.push(buildNovedadesMenuItem());
            // El maestro de SKU paso a colgar del grupo SKU (solo superadmin), y
            // Documentos ya no va aca: la pantalla quedo restringida a superadmin, e
            // isFinanzasOnly excluye ese perfil por definicion.
            return out;
        }

        if (hasProfile('customer_admin')) {
            out = [
              {
                label: 'sidebar.menu.home',
                type: 'section',
                children: [
                    hasSubscriptionFeature('pedidos') && {
                        uri: "/orders",
                        label: 'sidebar.menuItem.orders',
                        type: "nav-item",
                        icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    hasSubscriptionFeature('rutas') && {
                        uri: "/planification",
                        label: 'sidebar.menuItem.planification',
                        type: "nav-item",
                        icon: <GroupWorkOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    buildDashboardMenuItem(user.current_company?.id),
                    hasSubscriptionFeature('rutas') && {
                        uri: "/trips",
                        label: 'sidebar.menuItem.trips',
                        type: "nav-item",
                        icon: <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    canAccessWarehouse() && buildWarehouseMenuItem(),
                    canAccessWarehouse() && buildInventoryMenuItem(),
                    hasSubscriptionFeature('pickeo') && {
                        uri: "/task_authorizations",
                        label: 'sidebar.menuItem.task_authorizations',
                        type: "nav-item",
                        icon: <InventoryOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    canAccessDocuments() && buildDocumentsMenuItem()
                ]
              },
              {
                label: 'sidebar.menu.maintenance',
                type: 'section',
                children: [
                    {
                        uri: "/customers",
                        label: 'sidebar.menuItem.customers',
                        type: "nav-item",
                        icon: <PersonPinCircleOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    {
                        uri: "/centres",
                        label: 'Bodegas',
                        type: "nav-item",
                        icon: <WarehouseOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    {
                        uri: "/vehicles",
                        label: 'sidebar.menuItem.vehicles',
                        type: "nav-item",
                        icon: <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    canAccessUsers() && {
                        uri: "/users",
                        label: 'sidebar.menuItem.users',
                        type: "nav-item",
                        icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    canAccessDevices() && {
                        uri: "/dispositivos",
                        label: 'Dispositivos',
                        type: "nav-item",
                        icon: <PhonelinkLockOutlinedIcon sx={{ fontSize: 20 }} />
                    }
                ]
              },
              {
                label: 'sidebar.menu.admin',
                type: 'section',
                children: [
                    hasProfile('superadmin') && {
                        uri: "/whatsapp",
                        label: 'WhatsApp Inbox',
                        type: "nav-item",
                        icon: <WhatsAppIcon sx={{ fontSize: 20, color: "#25D366" }} />
                    },
                    hasProfile('superadmin') && {
                        uri: "/surveys",
                        label: 'sidebar.menuItem.surveys',
                        type: "nav-item",
                        icon: <QuizOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    hasProfile('superadmin') && {
                        uri: "/logs",
                        label: 'sidebar.menuItem.logs',
                        type: "nav-item",
                        icon: <FeaturedPlayListOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    hasProfile('superadmin') && {
                        uri: "/release_notes",
                        label: 'Publicar novedades',
                        type: "nav-item",
                        icon: <CampaignOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    {
                        uri: "/company_settings",
                        label: 'sidebar.menuItem.company_settings',
                        type: "nav-item",
                        icon: <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
                    }
                ]
              }
            ];
        } else if (isAdminAndDriver) {
            out[0].children.push(
                hasSubscriptionFeature('rutas') && {
                    uri: "/trips",
                    label: 'sidebar.menuItem.trips',
                    type: "nav-item",
                    icon: user.current_company?.id === 80 ?
                        <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                        <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                },
                {
                    uri: "/customers",
                    label: 'sidebar.menuItem.customers',
                    type: "nav-item",
                    icon: <PersonPinCircleOutlinedIcon sx={{ fontSize: 20 }} />
                }
            );

            out[0].children.push(buildDashboardMenuItem(user.current_company?.id));

            out[0].children.push(
                {
                    uri: "/vehicles",
                    label: user.current_company?.id === 80 ? 'sidebar.menuItem.barges' : 'sidebar.menuItem.vehicles',
                    type: "nav-item",
                    icon: user.current_company?.id === 80 ?
                        <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                        <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                },
                canAccessUsers() && {
                    uri: "/users",
                    label: 'sidebar.menuItem.users',
                    type: "nav-item",
                    icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />
                },
                canAccessDevices() && {
                    uri: "/dispositivos",
                    label: 'Dispositivos',
                    type: "nav-item",
                    icon: <PhonelinkLockOutlinedIcon sx={{ fontSize: 20 }} />
                }
            );
        } else if (isSupervisorAndDriver) {
            out[0].children.push(
                canAccessWarehouse() && buildWarehouseMenuItem(),
                canAccessWarehouse() && buildInventoryMenuItem(),
                hasSubscriptionFeature('rutas') && {
                    uri: "/trips",
                    label: 'sidebar.menuItem.trips',
                    type: "nav-item",
                    icon: user.current_company?.id === 80 ?
                        <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                        <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                },
                {
                    uri: "/customers",
                    label: 'sidebar.menuItem.customers',
                    type: "nav-item",
                    icon: <PersonPinCircleOutlinedIcon sx={{ fontSize: 20 }} />
                }
            );

            out[0].children.push(buildDashboardMenuItem(user.current_company?.id));

            out[0].children.push(
                {
                    uri: "/vehicles",
                    label: user.current_company?.id === 80 ? 'sidebar.menuItem.barges' : 'sidebar.menuItem.vehicles',
                    type: "nav-item",
                    icon: user.current_company?.id === 80 ?
                        <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                        <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                }
            );

            out.push({
                label: 'sidebar.menu.admin',
                type: "section",
                children: [
                    canAccessUsers() && {
                        uri: "/users",
                        label: 'sidebar.menuItem.users',
                        type: "nav-item",
                        icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    canAccessDevices() && {
                        uri: "/dispositivos",
                        label: 'Dispositivos',
                        type: "nav-item",
                        icon: <PhonelinkLockOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                    {
                        uri: "/company_settings",
                        label: 'sidebar.menuItem.company_settings',
                        type: "nav-item",
                        icon: <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
                    }
                ]
            });
        } else {
            if (user && user.current_company?.id !== 80) {
                if (out[0].children && !_.includes(profileNames, 'driver')) {
                    hasSubscriptionFeature('pedidos') && out[0].children.push({
                        uri: "/orders",
                        label: 'sidebar.menuItem.orders',
                        type: "nav-item",
                        icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 20 }} />
                    });
                }

                (!hasProfile('seller') || hasProfile('superadmin') || hasProfile('customer_admin') || hasProfile('supervisor')) && hasSubscriptionFeature('rutas') && out[0].children.push({
                    uri: "/planification",
                    label: 'sidebar.menuItem.planification',
                    type: "nav-item",
                    icon: <GroupWorkOutlinedIcon sx={{ fontSize: 20 }} />
                });
            }

            if (user && out[0].children && !_.includes(profileNames, 'driver')) {
                hasSubscriptionFeature('rutas') && out[0].children.push({
                    uri: "/trips",
                    label: 'sidebar.menuItem.trips',
                    type: "nav-item",
                    icon: user.current_company?.id === 80 ?
                        <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                        <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                });
                out[0].children.push({
                    uri: "/customers",
                    label: 'sidebar.menuItem.customers',
                    type: "nav-item",
                    icon: <PersonPinCircleOutlinedIcon sx={{ fontSize: 20 }} />
                });

                canAccessWarehouse() && out[0].children.push(buildWarehouseMenuItem());
                canAccessWarehouse() && out[0].children.push(buildInventoryMenuItem());

                out[0].children.push(buildDashboardMenuItem(user.current_company?.id));

                if (user.current_company?.id !== 80) {
                    hasSubscriptionFeature('pickeo') && out[0].children.push({
                        uri: "/task_authorizations",
                        label: 'sidebar.menuItem.task_authorizations',
                        type: "nav-item",
                        icon: <InventoryOutlinedIcon sx={{ fontSize: 20 }} />
                    });
                }

                canAccessDocuments() && out[0].children.push(buildDocumentsMenuItem());
            }

            // Sólo para supervisor "puro". Si además es customer_admin o superadmin,
            // el bloque de abajo arma una sección ADMINISTRACIÓN que es superconjunto
            // de esta, y tener las dos duplicaba el menú (Vehículos, Impresoras y
            // Usuarios aparecían dos veces, bajo dos títulos "ADMINISTRACIÓN").
            if (user && out[0].children && hasProfile('supervisor') && !hasProfile('customer_admin') && !hasProfile('superadmin')) {
                out.push({
                    label: 'sidebar.menu.admin',
                    type: "section",
                    children: [
                        {
                            uri: "/centres",
                            label: 'Bodegas',
                            type: "nav-item",
                            icon: <WarehouseOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        {
                            uri: "/vehicles",
                            label: user.current_company?.id === 80 ? 'sidebar.menuItem.barges' : 'sidebar.menuItem.vehicles',
                            type: "nav-item",
                            icon: user.current_company?.id === 80 ?
                                <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                                <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        canAccessUsers() && {
                            uri: "/users",
                            label: 'sidebar.menuItem.users',
                            type: "nav-item",
                            icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        canAccessDevices() && {
                            uri: "/dispositivos",
                            label: 'Dispositivos',
                            type: "nav-item",
                            icon: <PhonelinkLockOutlinedIcon sx={{ fontSize: 20 }} />
                        }
                    ]
                });
            }

            if (user && out[0].children && (_.includes(profileNames, 'customer_admin') || _.includes(profileNames, 'superadmin'))) {
                out.push({
                    label: 'sidebar.menu.admin',
                    type: "section",
                    children: [
                        {
                            uri: "/centres",
                            label: 'Bodegas',
                            type: "nav-item",
                            icon: <WarehouseOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        {
                            uri: "/vehicles",
                            label: user.current_company?.id === 80 ? 'sidebar.menuItem.barges' : 'sidebar.menuItem.vehicles',
                            type: "nav-item",
                            icon: user.current_company?.id === 80 ?
                                <DirectionsBoatTwoToneIcon fontSize="small" color={"primary"} /> :
                                <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        canAccessUsers() && {
                            uri: "/users",
                            label: 'sidebar.menuItem.users',
                            type: "nav-item",
                            icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        canAccessDevices() && {
                            uri: "/dispositivos",
                            label: 'Dispositivos',
                            type: "nav-item",
                            icon: <PhonelinkLockOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        hasProfile('superadmin') && {
                            uri: "/whatsapp",
                            label: 'WhatsApp Inbox',
                            type: "nav-item",
                            icon: <WhatsAppIcon sx={{ fontSize: 20, color: "#25D366" }} />
                        },
                        hasProfile('superadmin') && {
                            uri: "/surveys",
                            label: 'sidebar.menuItem.surveys',
                            type: "nav-item",
                            icon: <QuizOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        hasProfile('superadmin') && {
                            uri: "/logs",
                            label: 'sidebar.menuItem.logs',
                            type: "nav-item",
                            icon: <FeaturedPlayListOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                        hasProfile('superadmin') && {
                            uri: "/release_notes",
                            label: 'Publicar novedades',
                            type: "nav-item",
                            icon: <CampaignOutlinedIcon sx={{ fontSize: 20 }} />
                        },
                    ]
                });
            }

            if (user && out[0].children && (_.includes(profileNames, 'customer_admin') || _.includes(profileNames, 'superadmin') || _.includes(profileNames, 'supervisor'))) {
                out[out.length - 1].children.push(
                    {
                        uri: "/company_settings",
                        label: 'sidebar.menuItem.company_settings',
                        type: "nav-item",
                        icon: <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
                    },
                );
            }
        }

        // Va justo debajo de Viajes, que es desde donde se llega con el icono QR.
        if (hasProfile('superadmin') && out[0]?.children) {
            const tripsIndex = out[0].children.findIndex((item) => item?.uri === '/trips');
            out[0].children.splice(tripsIndex + 1 || out[0].children.length, 0, buildEtiquetasMenuItem());
        }

        out[0]?.children?.push(buildNovedadesMenuItem());

        if (isKargotools) out.push(buildUtilidadesSection());
    }
    return out;
};

export default menus;
